import axios from "axios";
import { func } from "prop-types";
import { isApprover } from "../utils";
const ENV = process.env;
const host = process.env.REACT_APP_API_ENDPOINT;

// Internal help/generic functions
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      // Does this cookie string begin with the name we want?
      if (cookie.substring(0, name.length + 1) === `${name}=`) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

function buildHeaders(authorize = false, extraHeaders = {}) {
  const token = window.localStorage.token;
  let headers = {
    Pragma: "no-cache",
    "Cache-Control": "no-cache",
  };
  if (authorize && token)
    headers = { ...headers, Authorization: `token ${token}` };
  return { ...headers, ...extraHeaders };
}

function get(uri, params = {}, options) {
  const opt = { method: "GET", params, headers: buildHeaders() };
  return axios
    .get(`${host}${uri}`, { ...opt, ...options })
    .then((response) => response.data);
}

function post(uri, body = {}) {
  const options = {
    headers: buildHeaders(false, { "X-CSRFToken": getCookie("csrftoken") }),
  };
  return axios
    .post(`${host}${uri}`, body, options)
    .then((response) => response.data);
}

export function authorizedGet({ uri, params = {}, options }) {
  const opt = {
    params,
    headers: buildHeaders(true),
  };
  return axios
    .get(`${host}${uri}`, { ...options, ...opt })
    .then((response) => response.data)
    .catch(function (error) {
      //console.log(error);
    });
}

export function authorizedGetWithoutHost({ uri, params = {}, options }) {
  const opt = {
    params,
    headers: buildHeaders(true),
  };
  return axios
    .get(`${uri}`, { ...options, ...opt })
    .then((response) => response.data);
}

export function authorizedDownload(url, fileName, fileType) {
  fetch(`${fileName}.${fileType}`).then((response) => {
    response.blob().then((blob) => {
      const fileURL = window.URL.createObjectURL(blob);
      let alink = document.createElement("a");
      alink.href = `${host}${url}`;
      alink.download = `${fileName}.${fileType}`;
      alink.click();
    });
  });
}

export function authorizedPost({ uri, params, body = {} }) {
  const options = {
    params,
    headers: buildHeaders(true, { "X-CSRFToken": getCookie("csrftoken") }),
  };

  return axios
    .post(`${host}${uri}`, body, options)
    .then((response) => response.data)
    .catch(function (error) {
      return error.response;
      // console.log(error)
    });
}

export function authorizedPatch({ uri, params, body = {} }) {
  const options = {
    params,
    headers: buildHeaders(true, { "X-CSRFToken": getCookie("csrftoken") }),
  };

  return axios
    .patch(`${host}${uri}`, body, options)
    .then((response) => response.data)
    .catch(function (error) {
      return error.response;
      // console.log(error)
    });
}

function authorizedPostWithoutHost({ uri, params, body = {} }) {
  const options = {
    params,
    headers: buildHeaders(true, { "X-CSRFToken": getCookie("csrftoken") }),
  };

  return axios.post(`${uri}`, body, options).then((response) => response.data);
}

export function authorizedDelete({ uri, params }) {
  const options = {
    params,
    headers: buildHeaders(true, { "X-CSRFToken": getCookie("csrftoken") }),
  };
  return axios.delete(`${host}${uri}`, options).then((response) => response);
}

export function authorizedDeleteWithBody({ uri, params, body = {} }) {
  const options = {
    params,
    headers: buildHeaders(true, { "X-CSRFToken": getCookie("csrftoken") }),
    data: body,
  };
  return axios
    .delete(`${host}${uri}`, options)
    .then((response) => response.data);
}

export function authorizedPut({ uri, params, body = {} }) {
  const options = {
    params,
    headers: buildHeaders(true, { "X-CSRFToken": getCookie("csrftoken") }),
  };
  return axios
    .put(`${host}${uri}`, body, options)
    .then((response) => response.data);
}

function authorizedPostUpload({ uri, body = {}, params }) {
  //console.log(uri, body, params, "params in the authorise post");
  const options = {
    params,
    headers: buildHeaders(true, {
      "content-type": "multipart/form-data",
      "X-CSRFToken": getCookie("csrftoken"),
    }),
  };
  return axios
    .post(`${host}${uri}`, body, options)
    .then((response) => response.data);
}

function authorizedPatchUpload({ uri, body = {}, params }) {
  const options = {
    params,
    headers: buildHeaders(true, {
      "content-type": "multipart/form-data",
      "X-CSRFToken": getCookie("csrftoken"),
    }),
  };
  return axios
    .patch(`${host}${uri}`, body, options)
    .then((response) => response.data);
}

export function authorizedFileDownload({ uri }) {
  const opt = {
    headers: buildHeaders(true),
    responseType: "blob",
  };
  return axios.get(`${host}${uri}`, { ...opt }).then((response) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;

    const filename_match =
      response.headers["content-disposition"].match(/filename="(.+)"/);
    if (filename_match) {
      link.setAttribute("download", filename_match[1]);
    } else {
      link.setAttribute("download", "export.pdf");
    }

    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  });
}

// REST API
export function registration(body) {
  return post("/api/auth/users/", body);
}

export function accountConfirmEmail(body) {
  return post("/api/auth/users/activation/", body);
}

export function passwordReset(body) {
  return post("/api/auth/users/custom_reset_password/", body);
}

export function passwordSet(body) {
  return post("/api/auth/users/set_password/", body);
}

export function passwordResetConfirm(body) {
  return post("/api/auth/users/reset_password_confirm/", body);
}
export function userConfirm(body) {
  return post("/api/auth/users/confirm/", body);
}

export function login(body) {
  return post("/api/v1/auth/token/login/", body);
}

export function JWTLogin(body) {
  return post("/api/auth/jwt/create/", body);
}

export function logout() {
  return get("/auth/logout/");
}

export function general() {
  return get("/api/v1/general/");
}

export function getUserData() {
  return authorizedGet({ uri: "/api/v1/me/" });
}

export function getUserProfileDetail({ id, params, options }) {
  return authorizedGet({
    uri: `/api/v1/accounts/user-profile-details/${id}/`,
    params,
    options,
  });
}

export function postUserData({ id, body }) {
  return authorizedPatch({ uri: `/api/v1/accounts/${id}/`, body });
}

export function getEducationData() {
  return authorizedGet({ uri: "/api/v1/accounts/education/" });
}

export function postEducationData(body) {
  return authorizedPost({ uri: "/api/v1/accounts/education/", body });
}

export function postAssessmentData(body) {
  return authorizedPost({ uri: "/api/assessment/", body });
}

export function createNewPageData(body) {
  return authorizedPost({ uri: "/api/assessment/page", body });
}

export function createNewQuestionData(body) {
  return authorizedPost({
    uri: `api/assessment/question/bank?assessment=14&page=18&order=1`,
    body,
  });
}

export function postBulkEducationData(body) {
  return authorizedPost({ uri: "/api/v1/accounts/bulk-edu-create/", body });
}

export function postLicenseData(body) {
  return authorizedPost({ uri: "/api/v1/accounts/product-license/", body });
}

export function patchEducationData({ id, body }) {
  return authorizedPatch({ uri: `/api/v1/accounts/education/${id}/`, body });
}

export function deleteEducationData(id) {
  return authorizedDelete({ uri: `/api/v1/accounts/education/${id}/` });
}

export function deleteAssessmentData(id) {
  return authorizedDelete({ uri: `/api/assessment/${id}` });
}

export function getProfessionalData() {
  return authorizedGet({ uri: "/api/v1/accounts/professional-info/" });
}
export function getProfessionalDataById(id) {
  return authorizedGet({ uri: `/api/v1/accounts/professional-info/${id}/` });
}

export function deleteDiscussionData(id) {
  return authorizedDelete({ uri: `/api/discussions/${id}/` });
}

export function postProfessionalData(body) {
  return authorizedPost({ uri: "/api/v1/accounts/professional-info/", body });
}
// Report - [Website] My Profile -> Professional Information -> Experience/Languages/Skills are not being saved
// Bug - BLI-1736
// Solution - Updated the api for pagination
export function postLangaugeData(body) {
  return authorizedGet({ uri: "/api/v1/languages/?mobile", body });
}
export function postSkillData(body) {
  return authorizedGet({ uri: "/api/v1/skills/?mobile", body });
}

export function getProfessionalDependentData(id) {
  return authorizedGet({
    uri: `/api/v1/accounts/user-profile-details/${id}/`,
  });
}

export function patchProfessionalData(id, body) {
  // return authorizedPatch({ uri: `/api/v1/accounts/professional-info/${id}/`, body })
  return authorizedPatch({
    uri: `/api/v1/accounts/user-organization-info/${id}/`,
    body,
  });
}

export function getLocationData() {
  return authorizedGet({ uri: "/api/v1/accounts/location/" });
}

export function postLocationData(body) {
  return authorizedPost({ uri: "/api/v1/accounts/location/", body });
}

export function patchLocationData(id, body) {
  return authorizedPatch({ uri: `/api/v1/accounts/location/${id}/`, body });
}

export function getAdditionalData() {
  return authorizedGet({ uri: "/api/v1/accounts/additional-info/" });
}

export function postAdditionalData(body) {
  return authorizedPost({ uri: "/api/v1/accounts/additional-info/", body });
}

export function patchAdditionalData(id, body) {
  return authorizedPatch({
    uri: `/api/v1/accounts/additional-info/${id}/`,
    body,
  });
}

export function getSocialData() {
  return authorizedGet({ uri: "/api/v1/accounts/social-media/" });
}

export function postSocialData(body) {
  return authorizedPost({ uri: "/api/v1/accounts/social-media/", body });
}

export function patchSocialData(id, body) {
  return authorizedPatch({ uri: `/api/v1/accounts/social-media/${id}/`, body });
}

export function userList(params, options) {
  return authorizedGet({ uri: `/api/v1/accounts/user-list/`, params, options });
}

export function addUserList(body) {
  return authorizedPostUpload({ uri: "/api/v1/accounts/add-user/", body });
}

export function getEvents() {
  return authorizedGet({ uri: "/api/events/" });
}
export function getEventsPost(body) {
  return authorizedPost({ uri: "/api/events/get-event/", body });
}
export function getEventsLinkData(id) {
  return authorizedGet({
    uri: "https://api.videosdk.live/v2/sessions/?roomId=" + id,
  });
}
export function getEventTypes() {
  return authorizedGet({ uri: "/api/events/Event-type/" });
}
export function deleteEvents(id) {
  return authorizedDelete({ uri: `/api/events/${id}/` });
}

export function editEventTypes({ id, body }) {
  return authorizedPatch({ uri: `/api/events/${id}/`, body });
}

export function getUsers() {
  return authorizedGet({ uri: "/api/v1/accounts/get-users/?search=&role=3,4" });
}

// export function getEventToken() {
//   return authorizedGetWithoutHost({
//     uri: "https://lmsapiconnect.brihha.in/get-token",
//   });
// }
export function getEventToken(body) {
  return authorizedPost({ uri: "/api/meeting/create", body });
}

export function getMeetingCreateClassroom(body) {
  return authorizedPost({ uri: "/api/meeting/create_classroom", body });
}

export function postAssessmentClassroomAssignUser(body) {
  return authorizedPost({ uri: "/api/classroom/attendee/", body });
}

export function postEventsClassroom(body) {
  return authorizedPost({ uri: "/api/classroom/", body });
}

export function deleteClass(id) {
  return authorizedDelete({ uri: `/api/classroom/${id}/` });
}

export function postEvents(body) {
  return authorizedPost({ uri: "/api/events/", body });
}

export function patchEvent(body) {
  return authorizedPatch({ uri: `/api/classroom/${body.id}/`, body });
}
export function postEventToken(body) {
  return authorizedPostWithoutHost({
    uri: "https://lmsapiconnect.brihha.in/create-meeting",
    body,
  });
}

export function deleteUser(id) {
  return authorizedDelete({ uri: `/api/v1/accounts/remove-user/${id}/` });
}

export function userAction({ id, body }) {
  return authorizedPatch({ uri: `/api/v1/accounts/user-action/${id}/`, body });
}

export function bulkUpload(body) {
  return authorizedPost({ uri: `/api/v1/accounts/user-bulk-upload/`, body });
}

export function getCourses(params, uri) {
  return authorizedGet({ uri: uri || "/api/courses/get-courses/", params });
}

export function getLernerCourses(params) {
  return authorizedGet({ uri: "/api/courses/", params });
}

export function getCourseCategory(params) {
  return authorizedGet({ uri: "/api/courses/categories/", params });
}

export function getallAssignCourses(params) {
  return authorizedGet({ uri: "/api/courses/get-all-assign-courses/", params });
}

export function getReferenceCourses(params) {
  return authorizedGet({
    uri: "/api/courses/get-created_by_superadmin-courses/",
    params,
  });
}

export function getCPDCourses(params, uri) {
  return authorizedGet({
    uri: uri || "/api/courses/get_cpd_courses/",
    params,
  });
}

export function getAllCreatedCourses(params, uri) {
  return authorizedGet({
    uri: uri || "/api/courses/get-all-created-courses/",
    params,
  });
}

export function getOrgCategory(params) {
  return authorizedGet({ uri: "/api/v1/accounts/organization/", params });
}

export function errorTest() {
  return get("/api/v1/error/");
}

export function addCategory(body) {
  return authorizedPostUpload({ uri: "/api/courses/categories/", body });
}

export function getCourseAdminDashboard() {
  return authorizedGet({ uri: "/api/courses/admin-dashboard/" });
}

export function getCourseDetail({ id, body }) {
  return authorizedGet({ uri: `/api/courses/${id}/`, body });
}

export function getCourseDiscussions(body) {
  return authorizedGet({ uri: `/api/forum/`, body });
}

export function getCourseDiscussion({ slug, body }) {
  return authorizedGet({ uri: `/api/forum/${slug}/`, body });
}

export function getCourseThread({ params }) {
  return authorizedGet({ uri: `/api/thread/`, params });
}

export function postCourseDiscussion(body) {
  return authorizedPost({
    uri: `/api/courses/course-discussion-question/`,
    body,
  });
}

export function postCourseDiscussionAnswer(body) {
  return authorizedPost({
    uri: `/api/courses/course-discussion-answer/`,
    body,
  });
}

export function postCourseDiscussionLikeDislike(body) {
  return authorizedPost({
    uri: `/api/courses/course-discussion-like/`,
    body,
  });
}

export function postCourseDiscussionReplyLikeDislike(body) {
  return authorizedPost({
    uri: `/api/courses/course-discussion-answer-like-dislike/`,
    body,
  });
}

export function postCourseEnrollment(body) {
  return authorizedPost({ uri: "/api/courses/user-enrolments/", body });
}
export function approvedCourseEnrollmentRequest(body) {
  return authorizedPost({
    uri: "/api/courses/approved_course_enrollment_request/",
    body,
  });
}
export function courseEnrollmentRequest(body) {
  return authorizedPost({
    uri: "/api/courses/course_enrollment_request/",
    body,
  });
}
export function courseEnrollmentRequestRejectByUser(body) {
  return authorizedPost({
    uri: "/api/courses/course_enrollment_request_reject_by_user/",
    body,
  });
}

export function patchCourseEnrollment(id, body) {
  return authorizedPatch({ uri: `/api/courses/user-enrolments/${id}/`, body });
}

export function getCourseFAQsList({ courseId, body }) {
  return authorizedGet({ uri: `/api/courses/faq-list/${courseId}/`, body });
}

export function getCourseReviews({ courseId, body }) {
  return authorizedGet({ uri: `/api/courses/reviews-list/${courseId}/`, body });
}

export function getLearnerCourseReview({ id }) {
  return authorizedGet({ uri: `/api/v1/learning_path/all-reviews/${id}/` });
}

export function getSearchCourse(name) {
  return authorizedGet({ uri: `api/courses/2/modules/?name=${name}` });
}

export function addfileRepo(body) {
  return authorizedPostUpload({ uri: "/api/courses/file-repo/", body });
}
export function addfileRepoTest(body) {
  return authorizedPostUpload({ uri: "/api/courses/test-file-repo/", body });
}

export function deletefile(id) {
  return authorizedDelete({ uri: `/api/courses/file-repo/${id}/` });
}
export function deleteCourse(id) {
  return authorizedDelete({ uri: `/api/courses/${id}/` });
}

export function deleteCategory(id) {
  return authorizedDelete({ uri: `/api/courses/categories/${id}/` });
}

export function addCourse(body) {
  return authorizedPostUpload({ uri: "/api/courses/", body });
}

export function editCourse({ id, body }) {
  return authorizedPatch({ uri: `/api/courses/${id}/`, body });
}

export function editCoursePublish({ id, body }) {
  return authorizedPatch({ uri: `/api/courses/published/${id}/`, body });
}

export function patchCategory({ id, body }) {
  return authorizedPatch({ uri: `/api/courses/categories/${id}/`, body });
}

export function patchFileRepo({ id, body }) {
  return authorizedPatch({ uri: `/api/courses/file-repo/${id}/`, body });
}

export function getCourseFileRepo(params) {
  return authorizedGet({ uri: "/api/courses/file-repo/", params });
}
export function getCourseFileRepoAudio(params) {
  return authorizedGet({
    uri: "/api/courses/file-repo/?file_type=audio",
    params,
  });
}
export function getCourseFileRepoCatelogue(params) {
  return authorizedGet({
    uri: "/api/courses/file-repo/?file_type=catalogue",
    params,
  });
}
export function getCourseModule({ courseId, params }) {
  return authorizedGet({ uri: `/api/courses/${courseId}/modules/`, params });
}
export function postCourseModule({ courseId, body }) {
  return authorizedPost({ uri: `/api/courses/${courseId}/modules/`, body });
}
export function patchCourseModule({ id, body }) {
  return authorizedPatch({ uri: `/api/courses/modules/${id}/`, body });
}
export function getCourseModuleWithID({ id, body }) {
  return authorizedGet({ uri: `/api/courses/modules/${id}/`, body });
}
export function deleteModule(courseId) {
  return authorizedDelete({ uri: `/api/courses/modules/${courseId}/` });
}

export function postFilePercentage(body) {
  return authorizedPost({ uri: `/api/courses/module-file-user-status/`, body });
}

export function getCourseFaq({ courseId }) {
  return authorizedGet({ uri: `/api/courses/faq-list/${courseId}/` });
}
export function postCourseFaq(body) {
  return authorizedPost({ uri: `/api/courses/faq/`, body });
}
export function patchCourseFaq({ id, body }) {
  return authorizedPatch({ uri: `/api/courses/faq/${id}/`, body });
}
export function deleteFaq(id) {
  return authorizedDelete({ uri: `/api/courses/faq/${id}/` });
}

export function getCountry(params, options) {
  return authorizedGet({ uri: `/api/v1/accounts/country/`, params, options });
}

export function getOrganization() {
  return authorizedGet({ uri: `/api/v1/accounts/organization/` });
}

export function getMasterLocation() {
  return authorizedGet({ uri: `/api/v1/accounts/master-location/` });
}
export function getLocation() {
  return authorizedGet({ uri: `/api/v1/accounts/org_locations/` });
}

export function deleteOrganization(id) {
  return authorizedDelete({ uri: `/api/v1/accounts/organization/${id}/` });
}

export function addOrganizationList(body) {
  return authorizedPost({ uri: "/api/v1/accounts/organization/", body });
}
export function addDesignation(body) {
  return authorizedPost({ uri: "/api/v1/accounts/designations/", body });
}

export function updateOrganization({ id, body }) {
  return authorizedPatch({ uri: `/api/v1/accounts/organization/${id}/`, body });
}

export function updateDepartment({ id, body }) {
  return authorizedPatch({
    uri: `/api/v1/accounts/department-organization-map/${id}/`,
    body,
  });
}

export function orgBasedDepartmentInConfigrution(body) {
  return authorizedGet({
    uri: `/api/v1/accounts/get_departments_by_organization/${parseInt(
      body?.organization[0]
    )}/`,
  });
}
export function departmentBasedDesignationInConf(id) {
  if (id != undefined) {
    return authorizedGet({
      uri: `/api/v1/accounts/get-designation-by-department/${id}/`,
    });
  }
}
export function departmentBasedlocationInConf(id) {
  if (id != undefined) {
    return authorizedGet({
      uri: `/api/v1/accounts/get-location-by-department/${id}/`,
    });
  }
}

export function updateDesgination({ id, body }) {
  return authorizedPatch({ uri: `/api/v1/accounts/designations/${id}/`, body });
}

export function addLocationList(body) {
  return authorizedPost({ uri: "/api/v1/accounts/org_locations/", body });
}

export function addLocationInConf(body) {
  return authorizedPost({
    uri: "/api/v1/accounts/master-organization-department-location/",
    body,
  });
}
export function updateLocationInConf({ id, body }) {
  return authorizedPatch({
    uri: `/api/v1/accounts/master-organization-department-location/${id}/`,
    body,
  });
}

export function addDesignationInConf(body) {
  return authorizedPost({
    uri: "/api/v1/accounts/department-organization-designation-map/",
    body,
  });
}
export function updateDesignationInConf({ id, body }) {
  return authorizedPatch({
    uri: `/api/v1/accounts/department-organization-designation-map/${id}/`,
    body,
  });
}

export function addDepartmentList(body) {
  return authorizedPost({
    uri: "/api/v1/accounts/department-organization-map/",
    body,
  });
}

export function updateLocation({ id, body }) {
  return authorizedPatch({
    uri: `/api/v1/accounts/org_locations/${id}/`,
    body,
  });
}

export function getStateDetail({ id, body }) {
  return authorizedGet({ uri: `/api/v1/accounts/state/`, body });
}

export function getUserDetail({ id, body }) {
  return authorizedGet({ uri: `/api/v1/accounts/get-user-list/`, body });
}

export function deleteDepartment(id) {
  return authorizedDelete({ uri: `/api/v1/accounts/departments/${id}/` });
}
export function deleteDepartmentInConf(id) {
  return authorizedDelete({
    uri: `/api/v1/accounts/department-organization-map/${id}/`,
  });
}

export function deleteLocation(id) {
  return authorizedDelete({ uri: `/api/v1/accounts/org_locations/${id}/` });
}

export function deleteLocationInConf(id) {
  return authorizedDelete({
    uri: `/api/v1/accounts/master-organization-department-location/${id}/`,
  });
}

export function deleteDesignation(id) {
  return authorizedDelete({ uri: `/api/v1/accounts/designations/${id}/` });
}
export function deleteDesignationInConf(id) {
  return authorizedDelete({
    uri: `/api/v1/accounts/department-organization-designation-map/${id}/`,
  });
}

export function postCourseWithoutIDModule({ body }) {
  return authorizedPost({ uri: `/api/courses/modules/`, body });
}

/* Configuration Assignment API */

export function getAssignUser() {
  return authorizedGet({ uri: `/api/courses/assignment/` });
}

export function addAssignCourse(body) {
  return authorizedPost({ uri: `/api/courses/assignment/`, body });
}

export function updateAssignCourse({ id, body }) {
  return authorizedPatch({ uri: `/api/​courses​/assigment​/{id}​/`, body });
}

export function deleteAssignUser(id) {
  return authorizedDelete({ uri: `/api/courses/assignment/${id}/` });
}

/* Configuration text API */

export function addTextCourse(body) {
  return authorizedPost({
    uri: `/api/courses/course-configuration-text/`,
    body,
  });
}

/* Configuration Option API */

export function addOptionCourse(body) {
  return authorizedPost({
    uri: `/api/courses/course-configuration-options/`,
    body,
  });
}
/* Learning Path  API */

export function addPath(body) {
  return authorizedPost({ uri: `/api/v1/learning-path/`, body });
}

export function getFilePath() {
  return authorizedGet({ uri: `/api/v1/learning-path/` });
}

export function deleteFilePath(id) {
  return authorizedDelete({ uri: `/api/v1/learning-path/${id}/` });
}

export function deleteAllFilePath(id) {
  return authorizedDelete({ uri: `/api/v1/learning-path/${id}/` });
}

export function getLearnerCourseDetail({ id }) {
  return authorizedGet({ uri: `/api/v1/learning_path_detail/${id}/` });
}

export function getModuleByCourse({ id, learning_path_id }) {
  return authorizedGet({
    uri: `/api/v1/learning_path_course_module/${learning_path_id}/${id}/`,
  });
}
export function publishLearningPath({ id, body }) {
  return authorizedPatch({
    uri: `/api/v1/learning_path_published/${id}/`,
    body,
  });
}

/* Learning Path Course Post API */

export function addPathCourse(body) {
  return authorizedPost({ uri: `/api/v1/learning-path-course/`, body });
}

/* Learning Path User Post API */

export function addPathUser(body) {
  return authorizedPost({ uri: `/api/v1/learning-path-user/`, body });
}

/* Learning Path Notification Post API */

export function addPathNotification(body) {
  return authorizedPost({ uri: `/api/v1/learning-path-notification/`, body });
}

export function deleteCoursePath(id) {
  return authorizedDelete({ uri: `/api/v1/learning-path-course/${id}/` });
}

export function deleteAllCoursePath(id) {
  return authorizedDelete({ uri: `/api/v1/learning-path-course/${id}/` });
}

export function deleteUserPath(id) {
  return authorizedDelete({ uri: `/api/v1/learning-path-user/${id}/` });
}

export function deleteAllUserPath(body) {
  return authorizedPost({
    uri: `/api/v1/learning-path-user/delete-multiple-user/`,
    body,
  });
}

export function updatePath({ id, body }) {
  return authorizedPut({ uri: `/api/v1/learning-path/${id}/`, body });
}

export function updateCourseModule({ id, body }) {
  return authorizedPut({ uri: `/api/v1/learning-path-course/${id}/`, body });
}

export function updateUserModule({ id, body }) {
  return authorizedPut({ uri: `/api/v1/learning-path-user/${id}/`, body });
}

//--------------------reports-------------------
export function getTopLearner(rolename, limit) {
  //?role_name=learner&limit=5
  return authorizedGet({
    uri: `/api/report/top-learner/?role_name=${rolename}&${limit}`,
  });
}

export function getStatisticsList() {
  return authorizedGet({ uri: `/api/report/user-statistic-report/` });
}
export function getTopCourse() {
  return authorizedGet({ uri: `/api/report/top-course/` });
}

export function timeSpentMonthlyChart() {
  return authorizedGet({ uri: `/api/report/user-time-spentmonthly-chart/` });
}
export function numberOfCompletionChart() {
  return authorizedGet({ uri: `/api/report/number-of-completion-chart/` });
}
export function numberOfLoginUserChart() {
  return authorizedGet({ uri: `/api/report/number-of-login-user-chart/` });
}
//------------------------course completion tab-----------------
export function courseCompletionPieChart(type) {
  return authorizedGet({
    uri: `/api/report/course-completion/?user_type=${type}`,
  });
}
export function courseCompletionLineChart(year) {
  return authorizedGet({
    uri: `/api/report/course-completion-trainer/?year=${year}`,
  });
}
export function progressReportChart(type) {
  return authorizedGet({
    uri: `/api/report/progress-report/?user_type=${type}`,
  });
}
export function userStatusReport(type) {
  return authorizedGet({ uri: `/api/report/user-status/?user_type=${type}` });
}
export function topUserReport(type) {
  return authorizedGet({ uri: `/api/report/top-user/?user_type=${type}` });
}

//----------------course Report--------------------------------------------
export function overAllCourseStatitics() {
  return authorizedGet({ uri: `/api/report/overall-course-statistics/` });
}
export function overAllCategoryStatitics(year) {
  return authorizedGet({
    uri: `/api/report/overall-category-statistics/?year=${year}`,
  });
}

export function getUserCourseReportList(type) {
  return authorizedGet({
    uri: `/api/report/user-course-report/?user_type=${type}`,
  });
}

export function getUserProgressReportList(type) {
  return authorizedGet({ uri: `/api/report/course-list-progress-report/` });
}
export function postCourseReview(body) {
  return authorizedPost({ uri: "/api/courses/reviews/", body });
}

export function postLearnerCourseReview(body) {
  return authorizedPost({ uri: "/api/v1/learning_path/reviews/", body });
}

export function postLearningPathProgress(body) {
  return authorizedPost({ uri: "/api/courses/module-file-user-status/", body });
}

export function updateUserCourseModule({ id, body }) {
  return authorizedPatch({ uri: `/api/courses/assignment/${id}/`, body });
}

export function postCourseContentProgress(body) {
  return authorizedPost({
    uri: "/api/courses/user-course-module-file-status/",
    body,
  });
}

export function postLearningPathContentProgress(body) {
  return authorizedPost({
    uri: "/api/courses/module-file-user-status/",
    body,
  });
}

export function addDiscussion(body) {
  return authorizedPostUpload({ uri: `/api/discussions/`, body });
}

export function getDiscussionType() {
  return authorizedGet({ uri: `/api/discussions/discussion_type/` });
}

export function editDiscussion({ id, body }) {
  return authorizedPatch({ uri: `/api/discussions/${id}/`, body });
}

export function addDiscussionComment(body) {
  return authorizedPost({ uri: `/api/discussions/answear/`, body });
}

export function addlikeDislike(body) {
  return authorizedPost({ uri: `/api/discussions/like-dislike/`, body });
}

export function addView(body) {
  return authorizedPost({ uri: `/api/discussions/views/`, body });
}

export function deleteDiscussionComment(id) {
  return authorizedDelete({ uri: `/api/discussions/answear/${id}/` });
}

export function updateNotification(body) {
  return authorizedPatch({ uri: `/api/notify/${body.id}/`, body });
}

export function notificationViwed(body) {
  return authorizedPatch({ uri: `/api/notify/`, body });
}

export function notificationRead(body) {
  return authorizedPatch({ uri: `/api/notify/${body.id}/`, body });
}

export function addAnnouncement(body) {
  return authorizedPost({ uri: `/api/announcements/`, body });
}

export function deleteAnnouncementData(id) {
  return authorizedDelete({ uri: `/api/announcements/${id}/` });
}

export function getassignToOrgDepDesUser(body) {
  return authorizedGet({
    uri: `/api/v1/accounts/assign-to-org-dep-des-user/`,
    body,
  });
}
export function deleteCourseAssignment(id) {
  return authorizedDelete({ uri: `/api/courses/assignment/${id}/` });
}

export function userCourseModuleFileStatus(body) {
  return authorizedPost({
    uri: `/api/courses/user-course-module-file-status/`,
    body,
  });
}

export function postProfessionalInfo({ id, body }) {
  return authorizedPatch({
    uri: `/api/v1/accounts/user-organization-info/${id}/`,
    body,
  });
}

export function deleteAssessmentPageData(id) {
  return authorizedDelete({ uri: `/api/assessment/page/${id}` });
}

export function deleteAssessmentQuestionData(id) {
  return authorizedDelete({ uri: `/api/assessment/question/bank/${id}` });
}

export function postAsessmentOption(body) {
  return authorizedPost({ uri: "/api/assessment/assessment_settings", body });
}

export function userBadgeRangeList() {
  return authorizedGet({
    uri: "/api/gamification/user-badge-range-list/",
  });
}

export function postAssessmentAssignUser(body) {
  return authorizedPost({
    uri: "/api/assessment/assessment_assign_user",
    body,
  });
}

export function postProctorAssignUser(body) {
  return authorizedPost({
    uri: "/api/assessment/proctor/",
    body,
  });
}

export function postReviewerAssignUser(body) {
  return authorizedPost({
    uri: "/api/assessment/reviewer/",
    body,
  });
}
export function postAssessmentText(body) {
  return authorizedPost({ uri: "/api/assessment/assessment_message", body });
}

export function getClassRoomEventTypes() {
  return authorizedGet({ uri: `/api/classroom/event/` });
}

export function getAllClassRooms() {
  return authorizedGet({ uri: `/api/classroom/` });
}

export function ckUploader(body) {
  return authorizedPost({
    uri: "/api/v1/multipurpose_image_upload/",
    body,
  });
}

export function deleteAssign(id) {
  return authorizedDelete({ uri: `/api/classroom/attendee/${id}/` });
}
export function getClassroomAdmin() {
  return authorizedGet({ uri: "/api/classroom/dashboard/admin/" });
}
export function getClassroomTrainer() {
  return authorizedGet({ uri: "/api/classroom/dashboard/trainer/" });
}
export function getClassroomCalendar() {
  return authorizedGet({ uri: "/api/classroom/calender/" });
}
export function postClassroomPrerequisite(body) {
  return authorizedPost({ uri: "/api/classroom/pre_post_requisite/", body });
}
export function postClassroomPrerequisiteImage(body) {
  return authorizedPost({
    uri: "/api/v1/multipurpose_multi_image_upload/",
    body,
  });
}
//leaderShipBoard--gamification

export function userBadgeLeaderboardList() {
  return authorizedGet({
    uri: `/api/gamification/user-badge-leaderboard-list/`,
  });
}
export function userBadgeList() {
  return authorizedGet({
    uri: `/api/gamification/user-badge-list/`,
  });
}

export function addBadge(body) {
  return authorizedPostUpload({
    uri: `/api/gamification/add-badge/`,
    body,
  });
}
export function getBadge() {
  return authorizedGet({
    uri: `/api/gamification/add-badge/`,
  });
}

export function updateBadge({ id, body }) {
  return authorizedPatch({
    uri: `/api/gamification/update-badge/${id}/`,
    body,
  });
}

export function deleteBadge(id) {
  return authorizedDelete({ uri: `/api/gamification/update-badge/${id}/` });
}

export function userBadgeListDataId(id) {
  return authorizedGet({
    uri: `/api/gamification/user-badge-list/?badge_id=${id}`,
  });
}

export function userDetails(id) {
  return authorizedGet({
    uri: `/api/gamification/user-detail/${id}/`,
  });
}

export function userPointsList() {
  return authorizedGet({
    uri: `/api/gamification/user-point-list/`,
  });
}

export function searchApi(query) {
  return authorizedGet({
    uri: `/api/gamification/user-badge-list/?query=${query}`,
  });
}

export function organisationList() {
  return authorizedGet({
    uri: "/api/gamification/orgranization-list/?mobile=true",
  });
}

export function organisationFilter(defaultBadgeId, org_id) {
  return authorizedGet({
    uri: `/api/gamification/user-badge-list/?badge_id=${defaultBadgeId}&org_id=${org_id}`,
  });
}

export function getAllClasses(params) {
  return authorizedGet({
    uri: "/api/classrooms/classes/",
    params,
  });
}

export function getTrainerByOrg(id) {
  return authorizedGet({
    uri: `/api/classrooms/list-trainer/?org_ids=${id}`,
  });
}
export function getOrganizationList(id) {
  return authorizedGet({
    uri: `/api/classrooms/list-org/`,
  });
}
export function addClasses(body) {
  return authorizedPost({
    uri: `/api/classrooms/classes/`,
    body,
  });
}

export function updateClasses(id, body) {
  return authorizedPatch({
    uri: `/api/classrooms/class-update-delete/${id}/`,
    body,
  });
}

export function deleteClasses(id) {
  return authorizedDelete({
    uri: `/api/classrooms/class-update-delete/${id}/`,
  });
}

export function getClassroomByClassId(classId) {
  return authorizedGet({
    uri: `/api/classrooms/class-update-delete/${classId}/?is_approver=${isApprover()}`,
  });
}

export function deleteTopic(id) {
  return authorizedDelete({
    uri: `/api/classrooms/topic-update-delete/${id}/`,
  });
}
export function updateTopic(body) {
  return authorizedPatch({
    uri: `/api/classrooms/topic-update-delete/${body.id}/`,
    body,
  });
}

export function getSelectedLearnerOrg(id) {
  return authorizedGet({
    uri: "/api/classrooms/list-learner-university/?id=" + id,
  });
}

export function organisationFilterBoard(org_id) {
  return authorizedGet({
    uri: `/api/gamification/user-badge-leaderboard-list/?org_id=${org_id}`,
  });
}

export function searchPoint(id, query) {
  return authorizedGet({
    uri: `/api/gamification/user-detail-point-search/${id}/?query=${query}`,
  });
}

export function searchBadge(id, query) {
  return authorizedGet({
    uri: `/api/gamification/user-detail-badge-search/${id}/?query=${query}`,
  });
}

export function userBadgeRangePagnation(uri) {
  return authorizedGet({
    uri: uri,
  });
}

export function searchPointUser(query) {
  return authorizedGet({
    uri: `/api/gamification/user-point-list/?query=${query}`,
  });
}
export function postClassTopic(body) {
  return authorizedPost({ uri: "/api/classrooms/topics/", body });
}
export function getClassRoomTopics(classroom_Id) {
  return authorizedGet({
    uri: `/api/classrooms/topics/?classroom_id=${classroom_Id}&is_approver=${isApprover()}`,
  });
}

export function getGroup(uri) {
  return authorizedGet({ uri: uri });
}

export function addGroup(body) {
  return authorizedPost({ uri: "/api/v1/accounts/organization_group/", body });
}

export function editGroup({ id, body }) {
  return authorizedPatch({
    uri: `/api/v1/accounts/organization_group/${id}/`,
    body,
  });
}

export function deleteGroupData(id) {
  return authorizedDelete({
    uri: `/api/v1/accounts/organization_group/${id}/`,
  });
}
export function postTopicSession(body) {
  return authorizedPost({ uri: "/api/classrooms/sessions/", body });
}
export function getClassrooms() {
  return authorizedGet({
    uri: `/api/classrooms/room/`,
  });
}

export function getTopicSessions(topic_Id) {
  return authorizedGet({
    uri: `/api/classrooms/sessions/?topic_id=${topic_Id}&is_approver=${isApprover()}`,
  });
}

export function uploadRequiste(body) {
  return authorizedPost({
    uri: "/api/classrooms/upload-requisites/",
    body,
  });
}

export function updateRequiste({ id, body }) {
  return authorizedPatch({
    uri: `/api/classrooms/update-requisites/${id}/`,
    body,
  });
}

export function getClassroomTrainerList(ids) {
  return authorizedGet({
    uri: `/api/classrooms/list-learner/?org_ids=${ids}`,
  });
}

export function updateTopicSession({ id, body }) {
  return authorizedPatch({
    uri: `/api/classrooms/session-update-delete/${id}/`,
    body,
  });
}

export function deleteSession(id) {
  return authorizedDelete({
    uri: `/api/classrooms/session-update-delete/${id}/`,
  });
}

export function checkSessions({ trainer_id, date }) {
  return authorizedGet({
    uri: `/api/classrooms/check-session/?trainer_id=${trainer_id}&date=${date}`,
  });
}

export function getClassroomDashboard(params) {
  return authorizedGet({ uri: "/api/classrooms/classroom-dashboard/", params });
}

export function publishClassRoom(body) {
  return authorizedPost({
    uri: "/api/classrooms/publish-classroom/",
    body,
  });
}

export function surveyList() {
  return authorizedGet({
    uri: "/api/classrooms/survey/",
  });
}

export function deleteAssigneForAssessment(id) {
  return authorizedDelete({
    uri: `/api/assessment/assessment_assign_user/${id.userId}?assessment=${id.id}`,
  });
}

export function uploadBulkQuestion(body) {
  return authorizedPost({ uri: `/api/assessment/bulk`, body });
}

export function deleteAssessmentQuestionDataforQuestionBank(id) {
  return authorizedDelete({ uri: `/api/assessment/question/${id}` });
}

export function sessionDataCount() {
  return authorizedGet({
    uri: `/api/classrooms/session-data-count/`,
  });
}

export function classRoomDashData() {
  return authorizedGet({
    uri: `/api/classrooms/classroom-dashboard/`,
  });
}

export function getCalendarDashBoard(params) {
  return authorizedGet({
    uri: `/api/classrooms/classroom-calendar/`,
    params,
  });
}

export function additionalData() {
  return authorizedGet({
    uri: `/api/classrooms/dashboard-additional-data/`,
  });
}

export function getUserRoleList() {
  return authorizedGet({
    uri: "/api/v1/accounts/role/",
  });
}

export function getUserList({ query, role, org, dep, des, location }) {
  return authorizedGet({
    uri: `/api/v1/accounts/user-list-by-queryparams/?query=${query}&role=${role}&org=${org}&dep=${dep}&des=${des}&location=${location}`,
  });
}

export function getUserListViaMaster(url) {
  return authorizedGet({
    uri: url,
  });
}

export function getModules() {
  return authorizedGet({
    uri: "/api/approvalflow/approval-module/",
  });
}
export function postApprovalModule(body) {
  return authorizedPost({
    uri: "/api/approvalflow/add-module-approvers/",
    body,
  });
}

export function getApprovalModulesData(params) {
  return authorizedGet({
    uri: "/api/approvalflow/add-module-approvers/",
    params,
  });
}
export function getApprovalRequests(body) {
  return authorizedGet({
    uri: "api/approvalflow/list-requests/?type=web",
    body,
  });
}
export function getApprovalSummary({ childModuleId, moduleId, requestsIds }) {
  return authorizedGet({
    uri: `/api/approvalflow/approval-summary/?child_module_id=${childModuleId}&module_id=${moduleId}&module_request_id=${requestsIds}`,
  });
}

export function patchApproveReject(body, requestId) {
  return authorizedPatch({
    uri: `/api/approvalflow/approve-reject/${requestId}/`,
    body,
  });
}

export function deleteApprovalModule(moduleId, body) {
  return authorizedDeleteWithBody({
    uri: `/api/approvalflow/module-approval-user-RUD/${moduleId}/`,
    body,
  });
}

export function patchApprovalModule(body, moduleId) {
  return authorizedPatch({
    uri: `/api/approvalflow/module-approval-user-RUD/${moduleId}/`,
    body,
  });
}
export function getResourceList(type) {
  return authorizedGet({
    uri: `/api/assessment/?type=${type}&courses=true`,
  });
}

export function updateAssessment({ id, body }) {
  return authorizedPut({ uri: `/api/assessment/${id}`, body });
}
export function getAssessmentById(id) {
  return authorizedGet({ uri: `/api/assessment/${id}` });
}

export function getGenderOptions() {
  return authorizedGet({ uri: "/api/v1/accounts/get_gender/" });
}

export function callHelpDesk() {
  return authorizedGet({
    uri: "/api/v1/accounts/help_desk/",
  });
}

export function callClassRoomReport() {
  return authorizedGet({
    uri: "/api/classrooms/class-report/",
  });
}

export function callClassRoomSessionReport(classroom_id) {
  return authorizedGet({
    uri: `/api/classrooms/session-report/?class_id=${classroom_id}`,
  });
}

export function getAdminAssessmentReportAll() {
  return authorizedGet({ uri: "/api/assessment/report/list?mobile" });
}
export function getAdminAssessmentReportFiltered(
  searchItem,
  dateRange,
  universityIds
) {
  return authorizedGet({
    uri: `/api/assessment/report/list?limit=15&search=${searchItem}&date_filter=${dateRange}&org_ids=${universityIds}`,
  });
}

export function getAdminAssessmentReportDetailAll(id) {
  return authorizedGet({
    uri: `/api/assessment/report/detail?mobile&assessment=${id}`,
  });
}
export function getAdminAssessmentReportDetailFiltered(
  id,
  searchItem,
  dateRange
) {
  return authorizedGet({
    uri: `/api/assessment/report/detail?assessment=${id}&limit=15&search=${searchItem}&date_filter=${dateRange}`,
  });
}

export function getLearnerAssessmentReportAll() {
  return authorizedGet({ uri: "/api/assessment/learner/report?mobile" });
}
export function getLearnerAssessmentReportFiltered(searchItem, dateRange) {
  return authorizedGet({
    uri: `/api/assessment/learner/report?limit=15&search=${searchItem}&date_filter=${dateRange}`,
  });
}

export function publishClassRoomAdmins(body) {
  return authorizedPost({
    uri: "/api/classrooms/publish/",
    body,
  });
}

export function callClassRoomReportAll() {
  return authorizedGet({
    uri: "/api/classrooms/class-report/?mobile=true",
  });
}

export function callClassRoomSessionReportAll(classroom_id) {
  return authorizedGet({
    uri: `/api/classrooms/session-report/?class_id=${classroom_id}&mobile=true`,
  });
}

export function getRequestCount(body) {
  return authorizedGet({
    uri: `/api/approvalflow/request-count/`,
    body,
  });
}
export function callSessionLearnerReportAll(session_id) {
  return authorizedGet({
    uri: `/api/classrooms/learner-report/?session_id=${session_id}`,
  });
}

export function getModuleTracking(id) {
  return authorizedGet({
    uri: `/api/v1/accounts/user-module-audit-history/?user=${id}&login_from=web`,
  });
}

export function postModuleTracking(body) {
  return authorizedPost({
    uri: "/api/v1/accounts/user-module-audit-history/",
    body,
  });
}

export function getAssessments() {
  return authorizedGet({
    uri: `/api/assessment/?type=Quiz&courses=true`,
  });
}

export function getCourseCertificates() {
  return authorizedGet({
    uri: `/api/certificate/drop-down/?module=course`,
  });
}
export function getFilterType() {
  return authorizedGet({
    uri: `/api/courses/filter_type/`,
  });
}

export function getFilterTypeAll() {
  return authorizedGet({
    uri: `/api/courses/filter_type/?type=all`,
  });
}

export const getModuleForApproval = async (module, functionality) => {
  const moduleList = await getModules();
  return moduleList?.result
    ?.find((el) => String(el.name).toLowerCase() === module)
    ?.functionality.find(
      (module) => String(module.value).toLowerCase() === functionality
    );
};

export function getScormTracking(file, email) {
  const origin = new URL(file?.file).origin;
  const url = `${origin}/webservice/rest/server.php?wstoken=4d35df53f94cab4c8e75d846bbd29765&moodlewsrestformat=json&attempt=1&wsfunction=mod_scorm_get_scorm_sco_tracks_all&scoid=${file?.sco_id}&email=${email}`;
  return axios.get(url).then((response) => response.data);
}

export function reportsImportClassroom(body) {
  return authorizedPostUpload({
    uri: "/api/classrooms/import-classroom/",
    body,
  });
}

export function reportsImportLearner(body) {
  return authorizedPostUpload({
    uri: "/api/classrooms/import-learner/",
    body,
  });
}
export function learnerSampleReport(sessionId) {
  return authorizedGet({
    uri: `/api/classrooms/learner-sample-report/?session_id=${sessionId}`,
  });
}
export function addFacilityRoom(body) {
  return authorizedPost({
    uri: "/api/classrooms/room/",
    body,
  });
}

export function deleteFacilityRoom(roomId) {
  return authorizedDelete({
    uri: `/api/classrooms/room-update-delete/${roomId}/`,
  });
}

export function updateFacilityRoom(body, roomId) {
  return authorizedPatch({
    uri: `/api/classrooms/room-update-delete/${roomId}/`,
    body,
  });
}

export function proctorDetail(id) {
  return authorizedGet({
    uri: `/api/assessment/proctor/detail?assessment=${id}`,
  });
}

export function sendPhotoForVerification(body) {
  return authorizedPost({ uri: `/api/proctor/proctoring_login`, body });
}

export function savePhotoForVerification(body) {
  return authorizedPost({ uri: `/api/proctor/update_file_repo`, body });
}
export function getUserCertificates() {
  return authorizedGet({
    uri: `/api/certificate/user-certificate/`,
  });
}

export function sendProctoringScreenshot(body) {
  return authorizedPost({ uri: `/api/proctor/upload_frame`, body });
}
export function updateProfileImage(body, userIdProfile) {
  return authorizedPatch({
    uri: `/api/v1/accounts/profile_image/${userIdProfile}/`,
    body,
  });
}

export function importUserForOrganization(body) {
  return authorizedPost({ uri: `/api/v1/accounts/user-bulk-upload/`, body });
}

export function importCourse(body) {
  return authorizedPost({
    uri: `/api/courses/course_import_by_file_upload/`,
    body,
  });
}

export function deleteEcommerceCourse(id) {
  return authorizedDelete({ uri: `/api/ecommerce/update/product/${id}/` });
}

export function getAssessmentSettingsById(id) {
  return authorizedGet({
    uri: `/api/assessment/assessment_settings?assessment=${id}`,
  });
}
export function deleteSocialWallComment(id) {
  return authorizedDelete({ uri: `/api/feed/delete/${id}/` });
}

export function scheduleReport(body) {
  return authorizedPost({
    uri: "/api/report/crone_job_email_setting/",
    body,
  });
}

export function rescheduleReport(body, id) {
  return authorizedPatch({
    uri: `/api/report/crone_job_email_setting/${id}/`,
    body,
  });
}

export function deleteScheduleReport(id) {
  return authorizedDelete({
    uri: `/api/report/crone_job_email_setting/${id}/`,
  });
}

export function addCPDPlan(body) {
  return authorizedPost({
    uri: "/api/report/user_planning/",
    body,
  });
}

export function updateCPDPlan(body, id) {
  return authorizedPatch({
    uri: `/api/report/user_planning/${id}/`,
    body,
  });
}

export function deleteCPDPlan(id) {
  return authorizedDelete({
    uri: `/api/report/user_planning/${id}/`,
  });
}

export function getDashboardPermissions() {
  return authorizedGet({
    uri: `/api/dashboard/get_permission/`,
  });
}

export function postDashboardPermissions(body) {
  return authorizedPost({
    uri: "/api/dashboard/get_permission/",
    body,
  });
}

export function addSkill(body) {
  return authorizedPost({
    uri: "/api/competency/competency/",
    body,
  });
}

export function updateSkill(body, id) {
  return authorizedPatch({
    uri: `/api/competency/update_competency/${id}/`,
    body,
  });
}

export function deleteSkill(id) {
  return authorizedDelete({
    uri: `/api/competency/update_competency/${id}/`,
  });
}

export function addRating(body) {
  return authorizedPost({
    uri: "/api/competency/competency_course/",
    body,
  });
}

export function updateRating(body) {
  return authorizedPatch({
    uri: `/api/competency/update_competency_course/`,
    body,
  });
}

export function addFramework(body) {
  return authorizedPost({
    uri: "/api/competency/competency_framework/",
    body,
  });
}

export function updateFramework(body, id) {
  return authorizedPatch({
    uri: `/api/competency/update_competency_framework/${id}/`,
    body,
  });
}

export function deleteFramework(id) {
  return authorizedDelete({
    uri: `/api/competency/update_competency_framework/${id}/`,
  });
}

export function addFrameworkSkill(body) {
  return authorizedPost({
    uri: "/api/competency/competency_framework_threshold/",
    body,
  });
}

export function updateFrameworkSkill(body) {
  return authorizedPatch({
    uri: `/api/competency/update_competency_framework_threshold/`,
    body,
  });
}

export function addJournal(body) {
  return authorizedPost({
    uri: "/api/report/journal/",
    body,
  });
}

export function updateJournal(body, id) {
  return authorizedPatch({
    uri: `/api/report/update_journal/${id}/`,
    body,
  });
}

export function deleteJournal(id) {
  return authorizedDelete({
    uri: `/api/report/update_journal/${id}/`,
  });
}

export function enrollCPDEvent(body) {
  return authorizedPost({
    uri: "/api/events/cpd_event_enroll/",
    body,
  });
}

export function getUserTodo() {
  return authorizedGet({ uri: "/api/search/to_do/" });
}

export function getSignOut() {
  return authorizedGet({ uri: "/api/v1/auth/token/logout/" });
}


export function copyAssessment(body) {
  return authorizedPost({ uri: "/api/assessment/make-copy", body });
}